import React, { useState, useRef, useEffect } from "react";
import { Send, Loader2, ArrowLeft, Plus, ArrowRight, FileText, Check, AlertCircle, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ChatArtifact } from "./ChatArtifact";
import { ChatDownloadsPanel } from "./ChatDownloadsPanel";
import { parseArtifacts, DocumentArtifact } from "@/lib/documentConverter";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
  artifacts?: DocumentArtifact[];
}

interface ChatConversationProps {
  conversationId: string | null;
  projectId: string;
  projectName?: string;
  onBack?: () => void;
  onConversationCreated: (conversationId: string) => void;
  isCompact?: boolean;
}

// Hybrid pricing: base credits + per 1K output tokens
const CLAUDE_MODELS = [
  { value: "claude-3-5-haiku-20241022", label: "Haiku 3.5", baseCredits: 50, perK: 5 },
  { value: "claude-sonnet-4-20250514", label: "Sonnet 4", baseCredits: 100, perK: 25 },
  { value: "claude-sonnet-4-5", label: "Sonnet 4.5", baseCredits: 150, perK: 50 },
  { value: "claude-opus-4-5-20251101", label: "Opus 4.5", baseCredits: 250, perK: 100 },
];

export const ChatConversation: React.FC<ChatConversationProps> = ({
  conversationId,
  projectId,
  projectName,
  onBack,
  onConversationCreated,
  isCompact = false,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [selectedModel] = useState("claude-sonnet-4-5");
  const [isUploading, setIsUploading] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (conversationId) {
      fetchMessages();
    } else {
      setMessages([]);
    }
  }, [conversationId]);

  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages, streamingContent]);

  const fetchMessages = async () => {
    if (!conversationId) return;
    
    try {
      const { data, error } = await supabase
        .from("chat_messages")
        .select("*")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      
      // Parse artifacts from existing messages
      const messagesWithArtifacts = (data || []).map(m => {
        const { cleanContent, artifacts } = parseArtifacts(m.content);
        return {
          ...m,
          role: m.role as "user" | "assistant",
          content: artifacts.length > 0 ? cleanContent : m.content,
          artifacts: artifacts.length > 0 ? artifacts : undefined,
        };
      });
      
      setMessages(messagesWithArtifacts);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const createConversation = async (firstMessage: string): Promise<string> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    const title = firstMessage.length > 50 
      ? firstMessage.substring(0, 50) + "..." 
      : firstMessage;

    const { data, error } = await supabase
      .from("chat_conversations")
      .insert({
        project_id: projectId,
        user_id: user.id,
        title,
      })
      .select()
      .single();

    if (error) throw error;
    return data.id;
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    setIsLoading(true);
    setStreamingContent("");

    try {
      let currentConversationId = conversationId;

      if (!currentConversationId) {
        currentConversationId = await createConversation(userMessage);
        onConversationCreated(currentConversationId);
      }

      const tempUserMessage: Message = {
        id: `temp-${Date.now()}`,
        role: "user",
        content: userMessage,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, tempUserMessage]);

      const { data: savedUserMessage, error: userMsgError } = await supabase
        .from("chat_messages")
        .insert({
          conversation_id: currentConversationId,
          role: "user",
          content: userMessage,
        })
        .select()
        .single();

      if (userMsgError) throw userMsgError;

      setMessages((prev) => 
        prev.map(m => m.id === tempUserMessage.id ? { ...savedUserMessage, role: savedUserMessage.role as "user" | "assistant" } : m)
      );

      const allMessages = [...messages, savedUserMessage].map(m => ({
        role: m.role,
        content: m.content,
      }));

      // Get auth token for streaming request
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      
      // Use streaming fetch
      const response = await fetch(`${supabaseUrl}/functions/v1/chat-ai`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: allMessages,
          projectId,
          model: selectedModel,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 402 || errorData.error === 'Insufficient credits') {
          toast({
            title: "Insufficient credits",
            description: `You need ${errorData.required || 'more'} credits but only have ${errorData.balance || 0}. Please purchase more credits.`,
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }
        throw new Error(errorData.error || `Error: ${response.status}`);
      }

      // Parse SSE stream
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let fullMessage = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            if (!data || data === '[DONE]') continue;

            try {
              const event = JSON.parse(data);
              
              if (event.type === 'text') {
                fullMessage += event.content;
                setStreamingContent(fullMessage);
              } else if (event.type === 'done') {
                fullMessage = event.full_message || fullMessage;
              } else if (event.type === 'error') {
                throw new Error(event.error);
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }

      // Process remaining buffer
      if (buffer.startsWith('data: ')) {
        const data = buffer.slice(6).trim();
        if (data && data !== '[DONE]') {
          try {
            const event = JSON.parse(data);
            if (event.type === 'text') {
              fullMessage += event.content;
            } else if (event.type === 'done') {
              fullMessage = event.full_message || fullMessage;
            }
          } catch (e) {
            // Skip invalid JSON
          }
        }
      }

      setStreamingContent("");

      // Parse artifacts from the response
      const { cleanContent, artifacts } = parseArtifacts(fullMessage);
      const displayContent = artifacts.length > 0 ? cleanContent : fullMessage;

      // Save assistant message (with full content including artifact tags for persistence)
      const { data: savedAssistantMessage, error: assistantMsgError } = await supabase
        .from("chat_messages")
        .insert({
          conversation_id: currentConversationId,
          role: "assistant",
          content: fullMessage, // Save full content with artifact tags
        })
        .select()
        .single();

      if (assistantMsgError) throw assistantMsgError;

      setMessages((prev) => [...prev, { 
        ...savedAssistantMessage, 
        role: savedAssistantMessage.role as "user" | "assistant",
        content: displayContent,
        artifacts: artifacts.length > 0 ? artifacts : undefined,
      }]);

      await supabase
        .from("chat_conversations")
        .update({ updated_at: new Date().toISOString() })
        .eq("id", currentConversationId);

    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      setStreamingContent("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Maximum file size is 10MB",
        variant: "destructive",
      });
      return;
    }

    const allowedTypes = ["text/plain", "text/markdown", "application/pdf", "application/json", "text/csv"];
    if (!allowedTypes.includes(file.type) && !file.name.endsWith(".md")) {
      toast({
        title: "Unsupported file type",
        description: "Please upload a TXT, MD, PDF, JSON, or CSV file",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const storagePath = `${user.id}/${projectId}/${fileName}`;

      // Upload to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from("chat-files")
        .upload(storagePath, file);

      if (uploadError) throw uploadError;

      // Upload to Claude Files API
      let anthropicFileId = null;
      try {
        toast({ title: "Syncing with Claude...", description: "Processing file for AI" });
        
        const { data: claudeResult, error: claudeError } = await supabase.functions.invoke(
          'upload-file-to-claude',
          {
            body: {
              storagePath,
              fileName: file.name,
              fileType: file.type,
            },
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
          }
        );

        if (claudeError) {
          console.error('Claude upload error:', claudeError);
          toast({ 
            title: "Warning", 
            description: "File saved but Claude sync failed. AI may not be able to read this file.",
            variant: "destructive",
          });
        } else if (claudeResult?.file_id) {
          anthropicFileId = claudeResult.file_id;
        }
      } catch (claudeErr) {
        console.error('Claude upload failed:', claudeErr);
      }

      // Save to database
      const { error: dbError } = await supabase
        .from("chat_project_files")
        .insert({
          project_id: projectId,
          user_id: user.id,
          file_name: file.name,
          file_type: file.type || "text/plain",
          file_size: file.size,
          storage_path: storagePath,
          anthropic_file_id: anthropicFileId,
        });

      if (dbError) throw dbError;

      toast({ 
        title: anthropicFileId ? "File uploaded & synced" : "File uploaded",
        description: anthropicFileId ? "Claude can now read this file" : undefined,
      });
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Collect all artifacts from messages for downloads panel
  const allArtifacts = messages
    .filter(m => m.artifacts && m.artifacts.length > 0)
    .flatMap(m => m.artifacts!);

  // Compact mode - simple "Start project" button (click handled by parent)
  if (isCompact) {
    return (
      <div className="flex items-center justify-between w-full h-12 text-muted-foreground pointer-events-none">
        <span>Start project</span>
        <ArrowRight className="h-4 w-4" />
      </div>
    );
  }

  // Full conversation view
  return (
    <div className="flex h-full bg-background">
      {/* Main chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header with back button */}
        {onBack && (
          <div className="flex items-center gap-3 p-4 border-b border-border">
            <Button variant="ghost" size="icon" onClick={onBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="font-semibold flex-1">{projectName || "Conversation"}</h1>
            <div className="px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-lg">
              <p className="text-xs text-primary flex items-center gap-1.5">
                <Lightbulb className="h-3 w-3" />
                <span><strong>Tip:</strong> Ask for Word, PDF, or Excel outputs!</span>
              </p>
            </div>
          </div>
        )}

        {/* Messages */}
        <ScrollArea className="flex-1 p-4" ref={scrollAreaRef}>
          <div className="space-y-4">
          {messages.length === 0 && !streamingContent ? (
            <div className="flex flex-col items-center justify-center h-full py-20 text-muted-foreground">
              <div className="mb-6 px-4 py-3 bg-primary/10 border border-primary/20 rounded-lg max-w-md">
                <p className="text-sm text-primary flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 flex-shrink-0" />
                  <span>
                    <strong>Tip!</strong> Ask Claude to format answers as documents — like Word, PDF, or Excel files you can download.
                  </span>
                </p>
              </div>
              <p className="text-lg">Start a new conversation</p>
              <p className="text-sm">Ask Claude anything about your project</p>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div key={message.id}>
                  <div
                    className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        message.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-foreground"
                      }`}
                    >
                      <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                    </div>
                  </div>
                  {/* Render artifacts below the message */}
                  {message.artifacts && message.artifacts.length > 0 && (
                    <div className="flex justify-start mt-2">
                      <div className="max-w-[80%] space-y-2">
                        {message.artifacts.map((artifact, idx) => (
                          <ChatArtifact key={idx} artifact={artifact} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {/* Streaming message */}
              {streamingContent && (
                <div className="flex justify-start">
                  <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-muted text-foreground">
                    <p className="whitespace-pre-wrap text-sm">{streamingContent}</p>
                    <span className="inline-block w-2 h-4 bg-foreground/50 animate-pulse ml-1" />
                  </div>
                </div>
              )}
              {/* Loading indicator when waiting for stream to start */}
              {isLoading && !streamingContent && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-2xl px-4 py-3">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </ScrollArea>

        {/* Input */}
        <div className="border-t border-border p-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-card border border-border rounded-xl p-4">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Reply..."
                className="resize-none min-h-[60px] border-0 shadow-none focus-visible:ring-0 p-0 text-base"
                rows={2}
              />
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".txt,.md,.pdf,.json,.csv"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 text-muted-foreground"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Plus className="h-5 w-5" />
                    )}
                  </Button>
                </div>
                <Button
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                  size="icon"
                  className="h-10 w-10 rounded-full"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Downloads sidebar - only show when there are artifacts */}
      {allArtifacts.length > 0 && (
        <div className="w-36 border-l border-border bg-muted/30 hidden md:block">
          <ChatDownloadsPanel artifacts={allArtifacts} />
        </div>
      )}
    </div>
  );
};
